import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LucideIcon, Heart, Brain, Baby, Activity, Droplet, Wind, Pill, FlaskConical, Stethoscope, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface TopicCardProps {
  id: string;
  name: string;
  questionCount: number;
  completionPercentage: number;
  icon?: string;
}

const iconMap: Record<string, LucideIcon> = {
  Heart,
  Brain,
  Baby,
  Activity,
  Droplet,
  Wind,
  Pill,
  FlaskConical,
  Stethoscope,
  BookOpen: Stethoscope, // Default fallback
};

export function TopicCard({
  id,
  name,
  questionCount,
  completionPercentage,
  icon = "Stethoscope",
}: TopicCardProps) {
  const navigate = useNavigate();
  const Icon = iconMap[icon] || Stethoscope;

  const getStatusColor = () => {
    if (completionPercentage === 100) return "text-success";
    if (completionPercentage >= 50) return "text-primary";
    return "text-muted-foreground";
  };

  const getStatusBadge = () => {
    if (completionPercentage === 100) {
      return <Badge className="bg-success text-success-foreground">Completed</Badge>;
    }
    if (completionPercentage >= 50) {
      return <Badge variant="secondary">In Progress</Badge>;
    }
    return <Badge variant="outline">Not Started</Badge>;
  };

  const handleClick = () => {
    navigate(`/study/${id}`);
  };

  const handleAnalyticsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/analytics/topic/${id}`);
  };

  return (
    <Card
      className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:-translate-y-1 group"
      onClick={handleClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg bg-primary/10 ${getStatusColor()}`}>
            <Icon className="h-8 w-8" />
          </div>
          {getStatusBadge()}
        </div>

        <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
          {name}
        </h3>

        <p className="text-sm text-muted-foreground mb-4">
          {questionCount} {questionCount === 1 ? "Question" : "Questions"}
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold text-foreground">
              {completionPercentage}%
            </span>
          </div>
          <Progress
            value={completionPercentage}
            className="h-2"
          />
        </div>

        <div className="mt-4 pt-4 border-t flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {completionPercentage > 0 ? 'Track your performance' : 'No data yet'}
          </span>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleAnalyticsClick}
            disabled={completionPercentage === 0}
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            Analytics
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
